import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Wallet } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { getGarantias, getReporteCaja, type Garantia, type ProyeccionCaja } from "@/lib/api";

/**
 * Reportes → Reporte de Caja (90 días). Reemplaza el HTML que generaba
 * Documentos/generar_reporte_caja.py desde el Archivo Maestro: aquí todo sale
 * del ETRM.
 *
 *  - Caja real al CORTE = saldo de las cuentas en Movimientos bancarios (último
 *    movimiento cargado), opcionalmente + inversión.
 *  - Después del corte, día por día, lo de Proyección de Caja (MXN y USD).
 *  - Lo proyectado con fecha ≤ corte NO se cuenta: ya debería estar en bancos
 *    (contarlo sería doble). Se avisa y se puede incluir como pendiente al corte.
 *  - Todo en MXN equivalente con el tipo de cambio de Configuración.
 */

const PISO_DEF = 5_000_000;
const DIAS = 90;

const nf0 = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 });
const millones = (v: number) => `${v < 0 ? "−" : ""}$${(Math.abs(v) / 1e6).toFixed(2)} M`;

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const DOW = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

// Fechas como 'YYYY-MM-DD' en UTC: sin corrimientos por zona horaria.
const aFecha = (iso: string) => new Date(`${iso}T00:00:00Z`);
const masDias = (iso: string, n: number) => {
  const d = aFecha(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const etiqueta = (iso: string) => {
  const d = aFecha(iso);
  return `${String(d.getUTCDate()).padStart(2, "0")}-${MESES[d.getUTCMonth()]}-${String(d.getUTCFullYear()).slice(2)}`;
};
const dow = (iso: string) => DOW[aFecha(iso).getUTCDay()];
const hoyIso = () => new Date().toLocaleDateString("en-CA");

type Evento = ProyeccionCaja & { eq: number; pendiente: boolean };
type Dia = {
  fecha: string;
  eventos: Evento[];
  mxn: number; // cierre del día
  usd: number;
  total: number; // MXN eq
};

function leerBool(clave: string, def: boolean): boolean {
  try {
    const v = localStorage.getItem(clave);
    return v == null ? def : v === "1";
  } catch {
    return def;
  }
}
function guardarBool(clave: string, v: boolean) {
  try {
    localStorage.setItem(clave, v ? "1" : "0");
  } catch {
    /* sin storage */
  }
}

export function ReporteCajaPage() {
  const q = useQuery({ queryKey: ["proyeccion-caja", "reporte"], queryFn: getReporteCaja });
  const qg = useQuery({ queryKey: ["garantias"], queryFn: getGarantias });
  const [inicioSel, setInicio] = useState<string | null>(null);
  const [conInversion, setConInversion] = useState(() => leerBool("reporte-caja-inversion", true));
  const [conPendientes, setConPendientes] = useState(() => leerBool("reporte-caja-pendientes", false));
  const [piso, setPiso] = useState(PISO_DEF);

  const tc = q.data?.tipo_cambio.mxn_por_usd ?? 0;
  const corte = q.data?.corte ?? null;
  const dia0 = corte ?? hoyIso();

  // Caja real al corte, por moneda.
  const real = useMemo(() => {
    const r = { MXN: 0, USD: 0, invMXN: 0, invUSD: 0 };
    for (const c of q.data?.cuentas ?? []) {
      r[c.moneda] += c.saldo;
      if (c.moneda === "MXN") r.invMXN += c.inversion;
      else r.invUSD += c.inversion;
    }
    return r;
  }, [q.data]);

  const items = useMemo(() => q.data?.items ?? [], [q.data]);
  const pendientes = useMemo(() => items.filter((p) => corte != null && p.fecha_pago <= corte), [items, corte]);
  const netoPend = pendientes.reduce((a, p) => a + (p.tipo === "Ingreso" ? 1 : -1) * p.monto * (p.moneda === "USD" ? tc : 1), 0);

  // Serie diaria desde el corte (día 0 = saldo real, + pendientes si se incluyen).
  const serie = useMemo<Dia[]>(() => {
    if (!q.data) return [];
    const porDia = new Map<string, Evento[]>();
    for (const p of items) {
      const pendiente = corte != null && p.fecha_pago <= corte;
      if (pendiente && !conPendientes) continue;
      const f = pendiente ? dia0 : p.fecha_pago;
      const ev = { ...p, pendiente, eq: p.monto * (p.moneda === "USD" ? tc : 1) };
      porDia.set(f, [...(porDia.get(f) ?? []), ev]);
    }
    const ultima = items.length ? items[items.length - 1].fecha_pago : dia0;
    const fin = [ultima, masDias(dia0, DIAS)].sort()[1];
    let mxn = real.MXN + (conInversion ? real.invMXN : 0);
    let usd = real.USD + (conInversion ? real.invUSD : 0);
    const out: Dia[] = [];
    for (let f = dia0; f <= fin; f = masDias(f, 1)) {
      const eventos = porDia.get(f) ?? [];
      for (const e of eventos) {
        const s = e.tipo === "Ingreso" ? e.monto : -e.monto;
        if (e.moneda === "USD") usd += s;
        else mxn += s;
      }
      out.push({ fecha: f, eventos, mxn, usd, total: mxn + usd * tc });
    }
    return out;
  }, [q.data, items, corte, dia0, conPendientes, conInversion, real, tc]);

  const ultimaFecha = serie.length ? serie[serie.length - 1].fecha : dia0;
  const defInicio = hoyIso() > dia0 && hoyIso() <= ultimaFecha ? hoyIso() : dia0;
  const inicio = inicioSel && inicioSel >= dia0 && inicioSel <= ultimaFecha ? inicioSel : defInicio;
  const fin = masDias(inicio, DIAS);

  const ventana = useMemo(() => serie.filter((d) => d.fecha >= inicio && d.fecha <= fin), [serie, inicio, fin]);
  // Movimientos DESPUÉS del día de inicio: caja inicio + ingresos − egresos = caja final.
  const eventosPeriodo = useMemo(
    () => ventana.filter((d) => d.fecha > inicio).flatMap((d) => d.eventos.map((e) => ({ ...e, dia: d.fecha }))),
    [ventana, inicio],
  );
  const ing = eventosPeriodo.filter((e) => e.tipo === "Ingreso");
  const egr = eventosPeriodo.filter((e) => e.tipo === "Egreso");
  const sum = (es: Evento[]) => es.reduce((a, e) => a + e.eq, 0);
  const top = (es: (Evento & { dia: string })[]) => [...es].sort((a, b) => b.eq - a.eq).slice(0, 5);

  const primero = ventana[0];
  const ultimo = ventana[ventana.length - 1];
  const minimo = ventana.reduce<Dia | null>((m, d) => (m == null || d.total < m.total ? d : m), null);

  // Semana L–V del día de inicio.
  const lunes = masDias(inicio, -((aFecha(inicio).getUTCDay() + 6) % 7));
  const semana = [0, 1, 2, 3, 4].map((i) => {
    const f = masDias(lunes, i);
    return { fecha: f, dia: serie.find((d) => d.fecha === f) ?? null };
  });
  const criticos = semana.filter((s) => s.dia && s.dia.total < 0);
  const bajos = semana.filter((s) => s.dia && s.dia.total >= 0 && s.dia.total < piso);

  const puntos = ventana.map((d) => ({ ...d, etiqueta: etiqueta(d.fecha) }));

  // Garantías (vigentes) y saldo deudor del préstamo Pant3ra (Contabilidad → Garantías y préstamos).
  const garantias = (qg.data?.garantias ?? []).filter((g) => g.activa);
  const saldoPant3ra = (qg.data?.prestamos ?? []).reduce(
    (a, p) => a + (p.tipo === "Préstamo" ? 1 : -1) * p.monto * (p.moneda === "USD" ? tc : 1), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Wallet className="w-5 h-5 text-slate-500" /> Reporte de Caja · 90 días
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Caja real al último movimiento cargado en{" "}
            <Link to="/movimientos-bancarios" className="text-blue-600 hover:underline">Movimientos bancarios</Link>{" "}
            y, a partir de ahí, lo de{" "}
            <Link to="/proyeccion-caja" className="text-blue-600 hover:underline">Proyección de Caja</Link>.
            Todo en MXN equivalente (USD ×{" "}
            <Link to="/tipo-cambio" className="text-blue-600 hover:underline">TC {tc.toFixed(2)}</Link>).
          </p>
        </div>
      </div>

      {q.isLoading && <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">Cargando…</div>}
      {q.isError && (
        <div className="bg-white border rounded-lg p-8 text-center text-red-600 text-sm">{(q.error as Error).message}</div>
      )}

      {q.data && (
        <>
          {/* Controles */}
          <div className="bg-white border rounded-lg p-4 flex flex-wrap items-end gap-4 text-sm">
            <label className="block">
              <span className="block text-xs text-slate-500 mb-1">Fecha de inicio</span>
              <input
                type="date"
                value={inicio}
                min={dia0}
                max={ultimaFecha}
                onChange={(e) => setInicio(e.target.value || null)}
                className="border rounded px-2 py-1.5 tabular-nums"
              />
            </label>
            <div>
              <span className="block text-xs text-slate-500 mb-1">Fin (inicio + {DIAS} días)</span>
              <div className="px-2 py-1.5 bg-blue-50 border border-blue-200 rounded tabular-nums font-medium text-blue-800">
                {etiqueta(fin)}
              </div>
            </div>
            <label className="block">
              <span className="block text-xs text-slate-500 mb-1">Piso de caja (MXN)</span>
              <input
                inputMode="numeric"
                value={nf0.format(piso)}
                onChange={(e) => setPiso(Number(e.target.value.replace(/[^\d]/g, "")) || 0)}
                className="border rounded px-2 py-1.5 tabular-nums w-36 text-right"
              />
            </label>
            <label className="inline-flex items-center gap-2 pb-1.5">
              <input
                type="checkbox"
                checked={conInversion}
                onChange={(e) => { setConInversion(e.target.checked); guardarBool("reporte-caja-inversion", e.target.checked); }}
              />
              Contar la inversión como caja
            </label>
            <button
              type="button"
              onClick={() => setInicio(null)}
              className="ml-auto border rounded px-3 py-1.5 text-slate-600 hover:bg-slate-50"
            >
              Volver a hoy
            </button>
          </div>

          {/* Pendientes: proyectado con fecha ≤ corte */}
          {pendientes.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <b>{pendientes.length} movimientos proyectados</b> tienen fecha igual o anterior al corte bancario (
                  {corte && etiqueta(corte)}), neto <b className="tabular-nums">{millones(netoPend)}</b> MXN eq. Si ya
                  ocurrieron, están en Movimientos bancarios y contarlos sería doble: bórralos de la Proyección de Caja.
                  Si no han llegado, reprograma su fecha o inclúyelos aquí como pendientes al corte.
                </div>
              </div>
              <label className="inline-flex items-center gap-2 ml-6">
                <input
                  type="checkbox"
                  checked={conPendientes}
                  onChange={(e) => { setConPendientes(e.target.checked); guardarBool("reporte-caja-pendientes", e.target.checked); }}
                />
                Incluirlos como pendientes al {corte && etiqueta(corte)}
              </label>
              <details className="ml-6">
                <summary className="cursor-pointer text-xs text-amber-800">Ver la lista</summary>
                <table className="mt-1 text-xs">
                  <tbody>
                    {pendientes.map((p) => (
                      <tr key={p.id}>
                        <td className="pr-3 tabular-nums">{etiqueta(p.fecha_pago)}</td>
                        <td className="pr-3">{p.concepto ?? "—"}</td>
                        <td className="pr-3">{p.tipo}</td>
                        <td className="text-right tabular-nums">{nf0.format(p.monto)} {p.moneda}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            </div>
          )}

          {/* KPIs */}
          {primero && ultimo && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
              <Kpi
                label="Caja al inicio"
                valor={millones(primero.total)}
                nota={`cierre del ${etiqueta(primero.fecha)}${primero.fecha === corte ? " (real)" : ""}`}
                borde="border-t-blue-600"
              />
              <Kpi
                label={`Caja proyectada (+${DIAS} días)`}
                valor={millones(ultimo.total)}
                nota={`al ${etiqueta(ultimo.fecha)}`}
                borde="border-t-violet-600"
                cls={ultimo.total < 0 ? "text-red-600" : "text-violet-700"}
              />
              <Kpi
                label="Punto mínimo"
                valor={millones(minimo?.total ?? 0)}
                nota={minimo ? `${etiqueta(minimo.fecha)} · ${dow(minimo.fecha)}` : ""}
                borde="border-t-amber-500"
                cls={(minimo?.total ?? 0) < 0 ? "text-red-600" : (minimo?.total ?? 0) < piso ? "text-amber-600" : "text-slate-900"}
              />
              <Kpi
                label="Ingresos del periodo"
                valor={millones(sum(ing))}
                nota={`${ing.length} movimientos`}
                borde="border-t-emerald-600"
                cls="text-emerald-700"
              />
              <Kpi
                label="Egresos del periodo"
                valor={millones(sum(egr))}
                nota={`${egr.length} movimientos`}
                borde="border-t-red-600"
                cls="text-red-600"
              />
              <Kpi
                label="Préstamos Pant3ra"
                valor={qg.data ? millones(saldoPant3ra) : "…"}
                nota="acumulado · saldo deudor"
                borde="border-t-cyan-600"
                cls="text-cyan-700"
              />
            </div>
          )}

          {/* Caja real al corte */}
          <div className="bg-white border rounded-lg p-4 text-sm">
            <div className="text-xs text-slate-500 mb-2">
              Caja real al corte {corte ? etiqueta(corte) : "(sin movimientos bancarios)"} · por cuenta
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-2">
              {q.data.cuentas.map((c) => (
                <div key={c.clave}>
                  <div className="text-xs text-slate-500">{c.clave}</div>
                  <div className="font-semibold tabular-nums">
                    {nf0.format(c.saldo)} {c.moneda}
                    {c.inversion !== 0 && (
                      <span className="text-xs font-normal text-slate-500"> + inversión {nf0.format(c.inversion)}</span>
                    )}
                  </div>
                </div>
              ))}
              <div className="ml-auto text-right">
                <div className="text-xs text-slate-500">Total MXN eq</div>
                <div className="font-semibold tabular-nums">{millones(serie[0]?.total ?? 0)}</div>
              </div>
            </div>
          </div>

          {/* Evolución total */}
          <div className="bg-white border rounded-lg p-4">
            <div className="text-sm font-semibold text-slate-800 mb-2">Evolución de la caja · MXN equivalente</div>
            <div style={{ width: "100%", height: 380 }}>
              <ResponsiveContainer>
                <ComposedChart data={puntos} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="etiqueta" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={60} minTickGap={12} />
                  <YAxis tick={{ fontSize: 12 }} width={64} tickFormatter={(v) => `$${((v as number) / 1e6).toFixed(0)}M`} />
                  <Tooltip content={<TooltipDia />} />
                  <ReferenceLine
                    y={piso}
                    stroke="#dc2626"
                    strokeDasharray="6 5"
                    label={{ value: `Piso ${millones(piso)}`, position: "insideTopRight", fontSize: 11, fill: "#dc2626" }}
                  />
                  <ReferenceLine y={0} stroke="#94a3b8" />
                  <Area type="monotone" dataKey="total" name="Total MXN eq" stroke="#0f172a" strokeWidth={2.5} fill="#2563eb" fillOpacity={0.08} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {qg.data && (
            <>
              <GarantiasColocadas garantias={garantias.filter((g) => g.direccion === "colocada")} tc={tc} />
              <GarantiasRecibidas garantias={garantias.filter((g) => g.direccion === "recibida")} tc={tc} />
            </>
          )}

          {/* Top 5 */}
          <div className="grid gap-3 lg:grid-cols-2">
            <TopTabla titulo="↑ Top 5 ingresos del periodo" eventos={top(ing)} cls="text-emerald-700" />
            <TopTabla titulo="↓ Top 5 egresos del periodo" eventos={top(egr)} cls="text-red-600" />
          </div>

          {/* Semana L–V */}
          <div className="bg-white border rounded-lg p-4">
            <div className="text-sm font-semibold text-slate-800 mb-3">Saldos de la semana · lunes a viernes</div>
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-5">
              {semana.map(({ fecha, dia }, i) => {
                const v = dia?.total;
                const tono =
                  v == null ? "bg-slate-50 border-slate-200 text-slate-400"
                  : v < 0 ? "bg-red-50 border-red-200 text-red-600"
                  : v < piso ? "bg-amber-50 border-amber-200 text-amber-600"
                  : "bg-emerald-50 border-emerald-200 text-emerald-700";
                const neto = dia?.eventos.reduce((a, e) => a + (e.tipo === "Ingreso" ? 1 : -1) * e.eq, 0) ?? 0;
                return (
                  <div key={fecha} className={`border border-t-4 rounded-lg p-3 ${tono}`}>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                      {["Lunes", "Martes", "Miércoles", "Jueves", "Viernes"][i]}
                    </div>
                    <div className="text-xs font-semibold text-slate-800">{etiqueta(fecha)}</div>
                    <div className="text-lg font-semibold tabular-nums mt-1">{v == null ? "—" : millones(v)}</div>
                    <div className="text-[11px] text-slate-500">
                      {dia == null ? "Fuera del rango" : dia.eventos.length
                        ? `${dia.eventos.length} mov. · neto ${neto >= 0 ? "+" : "−"}${millones(Math.abs(neto))}`
                        : "Sin movimientos"}
                    </div>
                  </div>
                );
              })}
            </div>
            {criticos.length > 0 ? (
              <div className="mt-3 p-2.5 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                <b>Día(s) crítico(s):</b>{" "}
                {criticos.map((s) => `${etiqueta(s.fecha)} (${millones(s.dia!.total)})`).join(", ")}. La caja queda en
                negativo: adelantar cobranza, diferir pagos o fondeo puente.
              </div>
            ) : bajos.length > 0 ? (
              <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                <b>Bajo el piso de {millones(piso)}:</b>{" "}
                {bajos.map((s) => `${etiqueta(s.fecha)} (${millones(s.dia!.total)})`).join(", ")}.
              </div>
            ) : (
              <div className="mt-3 p-2.5 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-800">
                Todos los días de la semana cierran arriba del piso de {millones(piso)}.
              </div>
            )}
          </div>

          {/* Por divisa */}
          <div className="bg-white border rounded-lg p-4">
            <div className="text-sm font-semibold text-slate-800 mb-2">Caja por divisa</div>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <ComposedChart data={puntos} margin={{ top: 8, right: 8, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="etiqueta" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={60} minTickGap={12} />
                  <YAxis yAxisId="mxn" tick={{ fontSize: 12, fill: "#2563eb" }} width={64} tickFormatter={(v) => `$${((v as number) / 1e6).toFixed(1)}M`} />
                  <YAxis yAxisId="usd" orientation="right" tick={{ fontSize: 12, fill: "#7c3aed" }} width={64} tickFormatter={(v) => `$${((v as number) / 1e3).toFixed(0)}K`} />
                  <Tooltip formatter={(v: number, name: string) => [`$${nf0.format(v)}`, name]} />
                  <Line yAxisId="mxn" type="monotone" dataKey="mxn" name="Caja MXN" stroke="#2563eb" strokeWidth={2.5} dot={false} />
                  <Line yAxisId="usd" type="monotone" dataKey="usd" name="Caja USD" stroke="#7c3aed" strokeWidth={2.5} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 text-xs text-slate-500">
              <span><span className="inline-block w-3 h-0.5 bg-blue-600 align-middle mr-1" />Caja MXN (eje izq.)</span>
              <span><span className="inline-block w-3 h-0.5 bg-violet-600 align-middle mr-1" />Caja USD (eje der.)</span>
            </div>
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
            Los USD se convierten a MXN con un tipo de cambio fijo de <b>{tc.toFixed(2)}</b>. Es una proyección basada en
            los compromisos capturados en Proyección de Caja; puede variar con el tipo de cambio real y las fechas
            efectivas de cobro y pago.
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, valor, nota, borde, cls = "text-slate-900" }: {
  label: string; valor: string; nota: string; borde: string; cls?: string;
}) {
  return (
    <div className={`bg-white border border-t-4 ${borde} rounded-lg p-3`}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-xl font-semibold tabular-nums mt-1 ${cls}`}>{valor}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{nota}</div>
    </div>
  );
}

function TopTabla({ titulo, eventos, cls }: { titulo: string; eventos: (Evento & { dia: string })[]; cls: string }) {
  return (
    <div className="bg-white border rounded-lg p-4">
      <div className={`text-xs font-semibold uppercase tracking-wide mb-2 ${cls}`}>{titulo}</div>
      <table className="w-full text-sm">
        <tbody>
          {eventos.map((e) => (
            <tr key={e.id} className="border-t first:border-t-0">
              <td className="py-1 pr-2 text-slate-500 tabular-nums whitespace-nowrap">{etiqueta(e.dia)}</td>
              <td className="py-1 pr-2">
                {e.concepto ?? "—"}
                {e.moneda === "USD" && <span className="text-xs text-slate-400"> · {nf0.format(e.monto)} USD</span>}
              </td>
              <td className={`py-1 text-right tabular-nums font-medium ${cls}`}>${nf0.format(e.eq)}</td>
            </tr>
          ))}
          {eventos.length === 0 && (
            <tr><td className="py-3 text-center text-slate-400 italic">Sin movimientos</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function TooltipDia({ active, payload }: { active?: boolean; payload?: { payload: Dia & { etiqueta: string } }[] }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border rounded shadow-sm p-2 text-xs max-w-xs">
      <div className="font-semibold">{d.etiqueta} · {dow(d.fecha)}</div>
      <div className="tabular-nums">Total: <b>{millones(d.total)}</b> MXN eq</div>
      <div className="tabular-nums text-slate-500">MXN {nf0.format(d.mxn)} · USD {nf0.format(d.usd)}</div>
      {d.eventos.length > 0 && (
        <div className="mt-1 border-t pt-1 space-y-0.5">
          {d.eventos.map((e) => (
            <div key={e.id} className={e.tipo === "Ingreso" ? "text-emerald-700" : "text-red-600"}>
              {e.tipo === "Ingreso" ? "+" : "−"}${nf0.format(e.monto)} {e.moneda} · {e.concepto ?? "—"}
              {e.pendiente && " (pendiente)"}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Días de hoy al vencimiento (negativo = ya venció). */
const diasA = (iso: string) => Math.round((aFecha(iso).getTime() - aFecha(hoyIso()).getTime()) / 86_400_000);
const mxnEq = (g: Garantia, tc: number) => g.monto * (g.moneda === "USD" ? tc : 1);
const montoTxt = (g: Garantia) => `$${nf0.format(g.monto)} ${g.moneda}`;

function Total({ texto, valor, cls = "text-slate-900", extra }: { texto: string; valor: string; cls?: string; extra?: string }) {
  return (
    <div className="mt-3 px-4 py-2.5 bg-slate-50 border rounded-lg flex justify-between items-center gap-3">
      <div className="text-sm text-slate-500">{texto}</div>
      <div className={`text-xl font-semibold tabular-nums ${cls}`}>
        {valor}
        {extra && <span className="text-xs font-normal text-slate-500"> · {extra}</span>}
      </div>
    </div>
  );
}

function Insignia({ texto, cls }: { texto: string; cls: string }) {
  return (
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${cls}`}>
      {texto.replace(/[^A-Za-zÁÉÍÓÚÑ]/g, "").slice(0, 3).toUpperCase()}
    </div>
  );
}

/** Garantías que coloca MPP, una tarjeta por contraparte; en rojo las que vencen en < 30 días. */
function GarantiasColocadas({ garantias, tc }: { garantias: Garantia[]; tc: number }) {
  const grupos = [...new Set(garantias.map((g) => g.contraparte))].map((c) => ({
    contraparte: c,
    filas: garantias.filter((g) => g.contraparte === c),
  }));
  const total = garantias.reduce((a, g) => a + mxnEq(g, tc), 0);
  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-slate-800">
          Garantías colocadas{grupos.length > 0 && ` · ${grupos.map((g) => g.contraparte).join(" e ")}`}
        </div>
        <Link to="/garantias" className="text-xs text-blue-600 hover:underline">Editar</Link>
      </div>
      {grupos.length === 0 && <div className="text-sm text-slate-400 italic">Sin garantías colocadas.</div>}
      <div className="grid gap-3 lg:grid-cols-2">
        {grupos.map(({ contraparte, filas }) => {
          const sub = filas.reduce((a, g) => a + mxnEq(g, tc), 0);
          const usd = filas.filter((g) => g.moneda === "USD").reduce((a, g) => a + g.monto, 0);
          const tipos = [...new Set(filas.map((g) => g.tipo).filter(Boolean))].join(" y ");
          const conceptos = [...new Set(filas.map((g) => g.concepto?.split(" · ")[0]).filter(Boolean))].join(", ");
          return (
            <div key={contraparte} className="bg-slate-50 border rounded-lg p-3">
              <div className="flex items-center gap-3 mb-2">
                <Insignia texto={contraparte} cls="bg-amber-100 text-amber-800" />
                <div>
                  <div className="text-sm font-semibold">{contraparte}</div>
                  <div className="text-[11px] text-slate-500">
                    {filas.length} garantía{filas.length !== 1 && "s"}{tipos && ` · ${tipos}`}{conceptos && ` · ${conceptos}`}
                  </div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-lg font-semibold tabular-nums">{millones(sub)}</div>
                  <div className="text-[10px] text-slate-500">
                    {usd > 0 ? `MXN eq · $${nf0.format(usd)} USD` : "MXN total"}
                  </div>
                </div>
              </div>
              <table className="w-full text-xs">
                <tbody>
                  {filas.map((g) => {
                    const dias = g.fecha_vencimiento ? diasA(g.fecha_vencimiento) : null;
                    const critica = dias != null && dias < 30;
                    return (
                      <tr key={g.id_garantia}>
                        <td className="py-1 text-slate-500">
                          {g.id_garantia} · {g.categoria ?? g.tipo ?? "—"}
                          {g.tipo === "SBLC" && g.categoria ? " (SBLC)" : ""}
                        </td>
                        <td className="py-1 text-right font-semibold tabular-nums">{montoTxt(g)}</td>
                        <td className={`py-1 pl-3 text-right text-[11px] whitespace-nowrap ${critica ? "text-red-600 font-semibold" : "text-slate-500"}`}>
                          {g.fecha_vencimiento ? `vence ${etiqueta(g.fecha_vencimiento)}` : g.vigencia ?? "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
      <Total texto="Total capital colocado en garantías" valor={`${millones(total)} MXN`} />
      <div className="mt-2 text-[11px] text-slate-500">En rojo, las garantías que vencen en menos de 30 días.</div>
    </div>
  );
}

/** Garantías que los clientes dan a favor de MPP, una tarjeta por garantía. */
function GarantiasRecibidas({ garantias, tc }: { garantias: Garantia[]; tc: number }) {
  const total = garantias.reduce((a, g) => a + mxnEq(g, tc), 0);
  const usd = garantias.filter((g) => g.moneda === "USD").reduce((a, g) => a + g.monto, 0);
  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-slate-800">Garantías recibidas de clientes · respaldo a favor de MPP</div>
        <Link to="/garantias" className="text-xs text-blue-600 hover:underline">Editar</Link>
      </div>
      {garantias.length === 0 && <div className="text-sm text-slate-400 italic">Sin garantías recibidas.</div>}
      <div className="grid gap-3 lg:grid-cols-2">
        {garantias.map((g) => {
          const detalle: [string, string | null][] = [
            ["Tipo", g.tipo],
            ["Modalidad", g.categoria],
            ["Equivalente", g.concepto],
            ["Inicio", g.fecha_inicio && etiqueta(g.fecha_inicio)],
            ["Vigencia", g.fecha_vencimiento ? `vence ${etiqueta(g.fecha_vencimiento)}` : g.vigencia],
          ];
          return (
            <div key={g.id_garantia} className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <div className="flex items-center gap-3 mb-2">
                <Insignia texto={g.contraparte} cls="bg-emerald-100 text-emerald-800" />
                <div>
                  <div className="text-sm font-semibold">{g.contraparte}</div>
                  <div className="text-[11px] text-slate-500">{[g.tipo, g.categoria].filter(Boolean).join(" · ")}</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-lg font-semibold tabular-nums text-emerald-700">{montoTxt(g)}</div>
                  {g.moneda === "USD" && (
                    <div className="text-[10px] text-slate-500">≈ {millones(mxnEq(g, tc))} MXN @{tc.toFixed(2)}</div>
                  )}
                </div>
              </div>
              <table className="w-full text-xs">
                <tbody>
                  {detalle.filter(([, v]) => v).map(([k, v]) => (
                    <tr key={k}>
                      <td className="py-1 text-slate-500 w-2/5">{k}</td>
                      <td className="py-1 font-semibold">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
      <Total
        texto="Total respaldo de clientes a favor de MPP"
        valor={`${millones(total)} MXN`}
        cls="text-emerald-700"
        extra={usd > 0 ? `$${nf0.format(usd)} USD` : undefined}
      />
    </div>
  );
}
